import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { EventBus, GameEvents } from '@/game/utils/EventBus'
import { toastVariants, useMotionVariants } from '../motion/motionTokens'

interface Notification {
  id: string
  message: string
  type: 'success' | 'warning' | 'info' | 'error'
  timestamp: number
}

export function NotificationSystem() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  
  const addNotification = useCallback((message: string, type: Notification['type'] = 'info') => {
    const notification: Notification = {
      id: crypto.randomUUID(),
      message,
      type,
      timestamp: Date.now(),
    }
    setNotifications((prev) => [...prev, notification])
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
    }, 4000)
  }, [])
  
  useEffect(() => {
    // Listen for game events
    const handlers = {
      [GameEvents.NOTIFY]: (p: { message: string; type?: Notification['type'] }) =>
        addNotification(p.message, p.type ?? 'info'),
      [GameEvents.COURT_PLACED]: () => addNotification('¡Pista lista!', 'success'),
      [GameEvents.DAY_CHANGED]: (data: { day: number }) => {
        if (data.day % 7 === 1) {
          addNotification('Comienza una nueva semana', 'info')
        }
      },
      [GameEvents.WEEK_CHANGED]: () => addNotification('Semana cerrada: gastos y salarios procesados', 'warning'),
      [GameEvents.VICTORY]: () => addNotification('¡Club de Elite alcanzado!', 'success'),
    }
    
    Object.entries(handlers).forEach(([event, handler]) => {
      EventBus.on(event, handler)
    })
    
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        EventBus.off(event, handler)
      })
    }
  }, [addNotification])
  
  const variants = useMotionVariants(toastVariants)

  return (
    <div className="absolute bottom-20 right-4 flex flex-col gap-2 max-w-xs" style={{ zIndex: 60 }}>
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            layout
            variants={variants}
            initial="initial"
            animate="enter"
            exit="exit"
            className={`toast-broadcast ${notification.type}`}
          >
            {notification.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
